import "reflect-metadata"

import { useEffect } from "react"
import { Helmet } from "react-helmet-async"
import { Outlet, useLocation } from "react-router-dom"
import { useIsClient } from "usehooks-ts"

import Footer from "@/components/footer"
import { Navbar } from "@/components/navigation"
import { TailwindSize } from "@/components/tailwind-size"
import { ThemeSidebarWrapper } from "@/components/theme-sidebar-wrapper"
import ToasterWrapper from "@/components/toaster-wrapper"
import { EventMonitoring } from "@/domains/events/components/event-monitoring"
import { WrongChainPopup } from "@/domains/network/components/wrong-chain-popup"
import { useChainConfig } from "@/hooks/use-chain-config"

import Providers from "./pages/providers"

import "./pages/globals.css"
import "katex/dist/katex.min.css"

export default function App() {
  const isClient = useIsClient()
  const chainConfig = useChainConfig()
  const location = useLocation()

  // Scroll to top on route change (like a full page load). Must be before any early return so hook order is stable.
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  // very important to keep the Boolean check here, otherwise the query will be undefined and the disclaimer will show
  const searchParams = new URLSearchParams(location.search)
  const noDisclaimer = Boolean(searchParams.get("noDisclaimer") === "true")

  /**
   * WARNING!
   * We render different content depending on which page it is on, and server side rendering does NOT support this!
   * We get hydration issues which really sucks...
   *
   * The brutal fix for now is to disable server side rendering
   */
  if (!isClient) {
    return null
  }

  const explicitPageControlPaths = ["/"]
  const showNavBar = !explicitPageControlPaths.some((path) => location.pathname === path)

  return (
    <>
      <Helmet>
        <title>SCALE | BASE</title>
        <meta name="viewport" content="minimum-scale=1, initial-scale=1, width=device-width" />
        <meta
          name="description"
          content={`Scale allows low cost, near 0 slippage trades on uncorrelated or tightly correlated assets built on ${chainConfig.name}.`}
        />
        <meta name="og:title" content="Scale" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="icon" href="/favicon-scale.png" />
      </Helmet>
      <TailwindSize />
      <Providers>
        <EventMonitoring />
        <WrongChainPopup />
        <div className="flex min-h-svh bg-p500">
          <div className="flex flex-1 flex-col justify-between">
            {showNavBar && <Navbar />}
            <Outlet />
            <ToasterWrapper />
            <Footer
              isLandingPage={location.pathname === "/"}
              isBlogPage={location.pathname.startsWith("/blog")}
            />
          </div>
          <ThemeSidebarWrapper />
        </div>
      </Providers>
    </>
  )
}
